import type { CalculatorCard } from '../types/calculator';

/**
 * Scores how well a query fuzzy-matches against a target string.
 * Characters must appear in order but not consecutively.
 * Returns 0 for no match; higher scores = better match.
 */
export function fuzzyScore(query: string, text: string): number {
  const q = query.toLowerCase();
  const t = text.toLowerCase();

  if (q.length === 0) return 0;
  if (t.includes(q)) return q.length * 10 + (t.startsWith(q) ? 5 : 0);

  let score = 0;
  let textIndex = 0;
  let consecutive = 0;
  let matched = 0;

  for (let i = 0; i < q.length; i++) {
    const char = q[i];
    let found = false;

    while (textIndex < t.length) {
      if (t[textIndex] === char) {
        matched++;
        consecutive++;
        // Consecutive match bonus
        score += 1 + consecutive;
        // Word boundary bonus (start of string or after space/separator)
        if (textIndex === 0 || /[\s\-_/]/.test(t[textIndex - 1])) {
          score += 3;
        }
        textIndex++;
        found = true;
        break;
      }
      consecutive = 0;
      textIndex++;
    }

    if (!found) return 0;
  }

  // Start-of-string bonus
  if (t[0] === q[0]) {
    score += 2;
  }

  return score;
}

/**
 * Filters and ranks calculators by fuzzy match against title and description.
 * Title matches are weighted 2x.
 */
export function fuzzySearchCalculators(
  query: string,
  calculators: CalculatorCard[]
): CalculatorCard[] {
  const trimmed = query.trim();
  if (trimmed.length === 0) return calculators;

  const scored = calculators
    .map((calc) => {
      const titleScore = fuzzyScore(trimmed, calc.title) * 2;
      const descScore = fuzzyScore(trimmed, calc.description);
      return { calc, score: titleScore + descScore };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  return scored.map((item) => item.calc);
}
